import { Injectable, inject, signal } from '@angular/core';

import {
  HapticsService,
  PreferencesService,
  type HapticImpact,
  type HapticNotification
} from '@vevents/native-services';

import { MeService } from '../services/me.service';

/**
 * MOB-100 Stage 26 — user-configurable haptics layer.
 *
 * The BRD specifies the mapping between user interactions and haptic
 * feedback (§4.11):
 *
 *   - Light impact: poll-vote submit, message send, scan success.
 *   - Medium impact: leaderboard rank-up.
 *   - Notification error: any error pattern.
 *
 * The user can disable haptics via Settings > Accessibility. The
 * preference is mirrored to the server-side `me.preferences` row by
 * MOB-100 Stage 22 per REQ-F-MOB-202 — the local
 * `mob.haptics.enabled.v1` preference acts as a write-through cache so
 * the value is available before the first network round-trip on cold
 * launch.
 *
 * Reads `HapticsService` from `@vevents/native-services` which already
 * no-ops gracefully on platforms without the underlying plugin (BRD
 * edge case: "haptics not supported on device → service no-ops").
 */
const ENABLED_KEY = 'mob.haptics.enabled.v1';

@Injectable({ providedIn: 'root' })
export class MobileHapticsService {
  private readonly haptics = inject(HapticsService);
  private readonly prefs = inject(PreferencesService);
  private readonly me = inject(MeService);

  private readonly _enabled = signal(true);
  readonly enabled = this._enabled.asReadonly();
  private hydrated = false;

  /**
   * Hydration order:
   *  1. Read the local cache so haptics fire correctly before the
   *     server preferences round-trip resolves.
   *  2. Best-effort fetch from `/api/mobile/me/preferences` and apply
   *     the server value. Server is authoritative — last-write-wins
   *     across the user's devices per the Stage 22 prompt.
   */
  async hydrate(): Promise<void> {
    if (this.hydrated) return;
    this.hydrated = true;
    const stored = await this.prefs.get(ENABLED_KEY);
    if (stored === '0') this._enabled.set(false);
    else if (stored === '1') this._enabled.set(true);

    try {
      const remote = await this.me.loadPreferences();
      if (remote) {
        this._enabled.set(remote.hapticsEnabled);
        await this.prefs.set(ENABLED_KEY, remote.hapticsEnabled ? '1' : '0');
      }
    } catch (err) {
      console.warn('[MobileHapticsService] Server preference hydrate failed:', err);
    }
  }

  /**
   * Update the local cache, repaint the signal immediately, and
   * write-through to the server (REQ-F-MOB-202). Failures are non-
   * blocking — the local toggle stays applied and a future hydrate
   * reconciles.
   */
  async setEnabled(next: boolean): Promise<void> {
    this._enabled.set(next);
    await this.prefs.set(ENABLED_KEY, next ? '1' : '0');
    try {
      await this.me.patchPreferences({ hapticsEnabled: next });
    } catch (err) {
      console.warn('[MobileHapticsService] Server sync failed:', err);
    }
  }

  /** Generic impact at a chosen intensity. No-op when haptics disabled. */
  async impact(intensity: HapticImpact = 'light'): Promise<void> {
    if (!this._enabled()) return;
    await this.haptics.impact(intensity);
  }

  async notify(kind: HapticNotification): Promise<void> {
    if (!this._enabled()) return;
    await this.haptics.notify(kind);
  }

  // ------------------------------------------------------------------
  // BRD-mapped feedback shortcuts. The semantic names make the BRD
  // interaction table directly grep-able from consuming code:
  // ------------------------------------------------------------------

  /** Light feedback on poll vote submit (BRD §4.11). */
  pollVoteSubmitted(): Promise<void> {
    return this.impact('light');
  }

  /** Light feedback on chat message send (BRD §4.11). */
  messageSent(): Promise<void> {
    return this.impact('light');
  }

  /** Light feedback on QR scan success (BRD §4.11). */
  scanSucceeded(): Promise<void> {
    return this.impact('light');
  }

  /** Medium feedback on leaderboard rank-up (BRD §4.11). */
  leaderboardRankUp(): Promise<void> {
    return this.impact('medium');
  }

  /** Error pattern on any failure surface (BRD §4.11). */
  error(): Promise<void> {
    return this.notify('error');
  }
}
